import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useState, useEffect } from "react";
import Button from "../ui/button";
import { AnimatePresence, motion } from "framer-motion";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import MicrosoftIconSVG from "@/assets/svg/microsoft-icon.svg";
import SnapchatIconSVG from "@/assets/svg/snapchat-icon.svg";
import CryptoIconSVG from "@/assets/svg/creator-shield/crypto.svg";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";

export default function PowerfulPartnership() {
  const { ref, isVisible } = useScrollReveal();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  const badges = [
    {
      name: "Official YouTube Certified",
      icon: YoutubeIconSVG,
      link: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/",
    },
    {
      name: "Microsoft Certified Partner",
      icon: MicrosoftIconSVG,
      link: "#",
    },
    {
      name: "Official Snap Partner",
      icon: SnapchatIconSVG,
      link: "#",
    },
    {
      name: "Crypto Payout Agency",
      icon: CryptoIconSVG,
      link: "#",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBadgeIndex((prev) => (prev + 1) % badges.length);
    }, 3000); // Change badge every 3 seconds

    return () => clearInterval(interval);
  }, [badges.length]);

  const current = badges[currentBadgeIndex];

  const platforms = [
    [
      { name: "Snapchat", logoGray:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-snapchat.svg", logoColor:"https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-snapchat.svg", hoverColor: "bg-[#FFFC00]" },
      { name: "Google", logoGray: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-google.svg", logoColor: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-google.svg", hoverColor: "bg-white", hasShadow: true },
      { name: "YouTube", logoGray: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-youtube.svg", logoColor:"https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-youtube.svg", hoverColor: "bg-[#FF0000]" },
    ],
    [
      { name: "Facebook", logoGray: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-facebook.svg", logoColor:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-facebook.svg", hoverColor: "bg-[#1877F2]" },
      { name: "Instagram", logoGray:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-instagram.svg", logoColor:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-instagram.svg", hoverColor: "gradient", gradientColors: ["#FFD521", "#FFD521", "#D7005A", "#B900B4", "#B900B4"] },
      { name: "TikTok", logoGray:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-tiktok.svg", logoColor:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-tiktok.svg", hoverColor: "bg-[#000000]" },
    ],
    [
      { name: "Twitch", logoGray:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-twitch.svg", logoColor:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-twitch.svg", hoverColor: "bg-[#9147FE]" },
      { name: "Microsoft", logoGray:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-microsoft.svg", logoColor:  "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-microsoft.svg", hoverColor: "bg-white", hasShadow: true },
      { name: "Video", logoGray: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-video.svg", logoColor: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-video.svg", hoverColor: "bg-[#FF3D13]" },
      { name: "Venntilate", logoGray: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/gray-venntilate.svg", logoColor: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/color-venntilate.svg", hoverColor: "bg-black", hasShadow: true },
    ],
  ];

  const stats = [
    { value: "$75M+", label: "Paid to Creators" },
    { value: "1M+", label: "Videos Protected" },
    { value: "6,000+", label: "Trusted Creators" },
    { value: "24/7", label: "Operations" },
  ];

  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } py-20 lg:py-24 px-4 xxs:px-6 xs:px-10 sm:px-10 md:px-12 lg:px-6 xl:px-8  bg-white`}
    >
      <div className="mx-auto space-y-12 max-w-7xl">
        <div className="flex-1 space-y-2">
          <p className="text-[#757575] text-xs xxs:text-lg xs:text-lg sm:text-lg md:text-lg lg:text-lg 4xl:text-[24px] font-medium mb-6 enfinity-subtext-font text-left">
          Who we work with          </p>
          <h2 className="text-2xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal leading-tight xxs:leading-[1.3] xs:leading-[1.3] sm:leading-[1.2] md:leading-[1.2] enfinity-header-font text-left mb-2">
            <span className="text-black">Celebrating </span>
            <span className="bg-gradient-to-r from-[#00B0FF] via-[#00b0ff] to-[#9C44E9] bg-clip-text text-transparent">
              5+ years
            </span>
          </h2>
          <h2 className="text-2xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal leading-tight xxs:leading-[1.3] xs:leading-[1.3] sm:leading-[1.2] md:leading-[1.2] enfinity-header-font text-left bg-gradient-to-r from-[#01d01e] via-[#00b0ff] to-[#9C44E9] bg-clip-text text-transparent">
            of powerful partnership
          </h2>
        </div>

        {/* Platform Logos Grid */}
        <div className="flex flex-col gap-6 bg-white">
          {platforms.map((row, rowIndex) => (
            <div key={rowIndex} className="grid xxs:grid-cols-1 xs:grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:flex gap-6 justify-start">
              {row.map((platform, colIndex) => {
                const getHoverStyle = () => {
                  if (platform.hoverColor === "gradient") {
                    return {
                      backgroundImage: 'linear-gradient(135deg, #FFD521 0%, #FFD521 20%, #D7005A 50%, #B900B4 80%, #B900B4 100%)',
                      boxShadow: "0px 2px 6px 0px rgba(0,0,0,0.1)"
                    };
                  }
                  
                  const colorMap = {
                    "bg-[#FFFC00]": "#FFFC00",
                    "bg-white": "#FFFFFF",
                    "bg-black": "#000000",
                    "bg-[#FF0000]": "#FF0000",
                    "bg-[#1877F2]": "#1877F2",
                    "bg-[#000000]": "#000000",
                    "bg-[#9147FE]": "#9147FE",
                    "bg-[#FF3D13]": "#FF3D13"
                  };
                  
                  const bgColor = colorMap[platform.hoverColor] || "#FFFFFF";
                  const shadow = platform.hasShadow 
                    ? "0px 4px 6px rgba(0,0,0,0.1)" 
                    : "0px 2px 6px rgba(0,0,0,0.1)";
                  
                  return {
                    backgroundColor: bgColor,
                    boxShadow: shadow
                  };
                };
                
                const cardId = `${rowIndex}-${colIndex}`;
                const isHovered = hoveredCard === cardId;
                
                // Custom sizing for specific platforms
                const getImageSize = () => {
                  switch (platform.name) {
                    case "Facebook":
                      return "max-w-[250px] max-h-[300px] w-auto h-auto object-contain";
                    case "Instagram":
                      return "max-w-[220px] max-h-[270px] w-auto h-auto object-contain";
                    case "Twitch":
                      return "max-w-[200px] max-h-[200px] w-auto h-auto object-contain";
                    case "TikTok":
                      return "max-w-[200px] max-h-[140px] w-auto h-auto object-contain";
                    case "Video":
                      return "max-w-[140px] max-h-[100px] w-auto h-auto object-contain";
                    case "Venntilate":
                      return "max-w-[200px] max-h-[80px] w-auto h-auto object-contain";
                    case "Microsoft":
                      return "max-w-[240px] max-h-[240px] w-auto h-auto object-contain";
                      case "Snapchat":
                        return "max-w-[200px] max-h-[240px] w-auto h-auto object-contain";
                    default:
                      return "max-w-[250px] max-h-[120px] w-auto h-auto object-contain";
                  }
                };
                
                return (
                  <div
                    key={cardId}
                    className="bg-[#f1f1f1] transition-all duration-300 flex items-center justify-center px-[57.311px] py-[54.397px] rounded-[20px] w-full h-[210px] cursor-pointer overflow-hidden"
                    onMouseEnter={(e) => {
                      setHoveredCard(cardId);
                      const hoverStyle = getHoverStyle();
                      Object.assign(e.currentTarget.style, hoverStyle);
                    }}
                    onMouseLeave={(e) => {
                      setHoveredCard(null);
                      e.currentTarget.style.backgroundColor = "";
                      e.currentTarget.style.backgroundImage = "";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <img
                      src={isHovered ? platform.logoColor : platform.logoGray}
                      alt={platform.name}
                      className={`${getImageSize()} transition-opacity duration-50`}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Stats Container */}
        <div className="bg-[#1a1a1a] border border-[#eee] rounded-[20px] overflow-hidden flex flex-col justify-center items-center gap-7 w-full py-10">
          {/* Stats Row */}
          <div className="flex xxs:flex-col xs:flex-col sm:flex-col md:flex-col lg:flex-row items-center xxs:gap-6 xs:gap-6 sm:gap-6 md:gap-6 lg:justify-between w-full xxs:px-4 xs:px-6 sm:px-8 md:px-10 lg:px-40">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="flex flex-col gap-[5px] items-center xxs:w-full xs:w-full sm:w-full md:w-full lg:w-[117px]"
              >
                <p
                  className="xxs:text-[40px] xs:text-[45px] sm:text-[50px] md:text-[52px] lg:text-[55px] font-normal bg-gradient-to-b from-[#00d115] via-[#00b0ff] to-[#9c44e9] bg-clip-text text-transparent enfinity-header-font"
                  style={{ WebkitTextFillColor: "transparent" }}
                >
                  {stat.value}
                </p>
                <p className="text-[15px] font-medium text-white text-center whitespace-nowrap enfinity-subtext-font">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          {/* Horizontal Line */}
          <div className="flex flex-col items-center justify-center gap-7 w-full xxs:px-4 xs:px-6 sm:px-8 md:px-12 lg:px-16">
          <div className="w-full h-px bg-[#dddddd]"></div>

{/* Join Text */}
<div className="flex xxs:flex-col xs:flex-col sm:flex-col md:flex-col lg:flex-row xxs:items-center xs:items-center sm:items-center md:items-center lg:justify-between xxs:gap-4 xs:gap-4 sm:gap-4 md:gap-4 lg:gap-0 item-center w-full enfinity-subtext-font">
<div className="flex xxs:flex-col xs:flex-col sm:flex-col md:flex-row lg:flex-row items-center gap-5">
  <img
    src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/creatorss.png"
    alt="Creator"
    className="xxs:w-[350px] xs:w-[350px] sm:w-[px] md:w-[px] lg:w-[350px] 4xl:w-[400px] object-contain"
  />
  
</div>

  <a
    href={current.link || "#"}
    target={current.link && current.link !== "#" ? "_blank" : undefined}
    rel={current.link && current.link !== "#" ? "noopener noreferrer" : undefined}
  >
    <div className="relative h-14 w-fit overflow-hidden !mb-2 xxs:mt-0 xs:mt-0 sm:mt-0 md:mt-0 lg:mt-4">
      <AnimatePresence mode="wait">
        <motion.div
          key={current.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className={`absolute flex items-center gap-2 rounded-full ${current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" ? "py-2" : "py-1"} px-4 sm:px-3 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge`}
        >
          <img
            src={current.icon}
            alt={current.name}
            className={
              current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" 
                ? "h-6 w-6" 
                : current.name === "Official Snap Partner"
                ? "h-8 w-6"
                : "h-8 w-8"
            }
          />
          <span className="xxs:text-xs xs:text-xs sm:text-sm md:text-sm lg:text-sm enfinity-subtext-font">{current.name}</span>
          <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
        </motion.div>
      </AnimatePresence>
    </div>
  </a>
</div>
          </div>
          
        </div>
      </div>
    </div>
  );
}
